import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBadge } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  COLUMN_PROPERTIES,
  type ColumnProperties,
} from 'lumen/components/hotel/settings/central-mapping/tables/table-mapping';
import SettingsCentralMappingDropdownsDropdownBarBased from 'lumen/components/settings/central-mapping/dropdowns/dropdown-bar-based';
import SettingsCentralMappingDropdownsDropdownCommissionable from 'lumen/components/settings/central-mapping/dropdowns/dropdown-commissionable';
import SettingsCentralMappingDropdownsDropdownCostProfile from 'lumen/components/settings/central-mapping/dropdowns/dropdown-cost-profile';
import SettingsCentralMappingDropdownsDropdownMapping from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping';
import SettingsCentralMappingTablesCellsEditableNumberCell from 'lumen/components/settings/central-mapping/tables/cells/editable-number-cell';
import SettingsCentralMappingTablesCellsEditableTextCell from 'lumen/components/settings/central-mapping/tables/cells/editable-text-cell';
import type {
  CentralMappingCategory,
  CentralMappingMappingEntryChannel,
  CentralMappingMappingEntryMarketSegment,
  CostSettingProfile,
} from 'lumen/models';
import type {
  BarBased,
  Commissionable,
} from 'lumen/models/central-mapping/mapping-entry-market-segment';
import { isMappingEntryRoomType } from 'lumen/models/central-mapping/mapping-entry-room-type';
import {
  type Breakdown,
  BREAKDOWNS,
  type CentralMappingMappingEntryExtendedModel,
  mappingEntryHasBarBased,
  mappingEntryHasCommissionable,
  mappingEntryHasCostProfile,
  MAX_ADULTS_IN_ROOM,
  PMS_VALUE_TYPES,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import {
  aggregateBulkCostProfile,
  type AggregatedCostProfile,
} from 'lumen/utils/central-mapping-cost-profile';

interface MarketSettingsCentralMappingTablesTableMappingBulkEditRowSignature {
  Args: {
    barBased: {
      setBarBased: (value: BarBased) => void;
    };
    breakdown: Breakdown;
    bulkEditRow: {
      numberOfSelectedRows: number;
    };
    commissionable: {
      setCommissionable: (value: Commissionable) => void;
    };
    costProfile: {
      onNavigateToCostSettings: () => void;
      profiles: CostSettingProfile[];
      setCostProfile: (costProfile: CostSettingProfile | null) => void;
    };
    mapping: {
      hierarchyCategories: CentralMappingCategory[];
      setCategory: (category: CentralMappingCategory | null) => void;
    };
    selectedMappingEntries: CentralMappingMappingEntryExtendedModel[];
    valueType: PmsValueType;
  };
}

export default class MarketSettingsCentralMappingTablesTableMappingBulkEditRow extends Component<MarketSettingsCentralMappingTablesTableMappingBulkEditRowSignature> {
  @service declare centralMapping: Services['central-mapping'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get aggregatedCostProfile(): AggregatedCostProfile {
    return aggregateBulkCostProfile(this.selectedMappingEntriesHasCostProfile);
  }

  get bulkAutoBarBased(): BarBased {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstBarBased = selectedMappingEntries[0].autoBarBased;

    if (
      selectedMappingEntries.every(
        (entry) => entry.autoBarBased === firstBarBased,
      )
    ) {
      return firstBarBased;
    }

    return null;
  }

  get bulkAutoCategory(): CentralMappingCategory | null {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstAutoCategory = selectedMappingEntries[0].autoCategory;

    if (
      selectedMappingEntries.every(
        (entry) => entry.autoCategory === firstAutoCategory,
      )
    ) {
      return firstAutoCategory;
    }

    return null;
  }

  get bulkAutoCommissionable(): Commissionable {
    const { selectedMappingEntriesHasCommissionable: selectedMappingEntries } =
      this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstCommissionable = selectedMappingEntries[0].autoCommissionable;

    if (
      selectedMappingEntries.every(
        (entry) => entry.autoCommissionable === firstCommissionable,
      )
    ) {
      return firstCommissionable;
    }

    return null;
  }

  get bulkBarBased(): BarBased {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstBarBased = selectedMappingEntries[0].barBased;

    if (
      selectedMappingEntries.every((entry) => entry.barBased === firstBarBased)
    ) {
      return firstBarBased;
    }

    return null;
  }

  get bulkCategory(): CentralMappingCategory | null {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstCategory = selectedMappingEntries[0].category;

    if (
      selectedMappingEntries.every((entry) => entry.category === firstCategory)
    ) {
      return firstCategory;
    }

    return null;
  }

  get bulkCommissionable(): Commissionable {
    const { selectedMappingEntriesHasCommissionable: selectedMappingEntries } =
      this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstCommissionable = selectedMappingEntries[0].commissionable;

    if (
      selectedMappingEntries.every(
        (entry) => entry.commissionable === firstCommissionable,
      )
    ) {
      return firstCommissionable;
    }

    return null;
  }

  get bulkUserBarBased(): BarBased {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    // Edge case: when the barBase values are the same, but it's a mix of user-mapped and auto-mapped, the bulk edit dropdown should show the barBase as user-mapped.
    // That's why we check on `barBase` here and not `userbarBase`.
    const firstBarBased = selectedMappingEntries[0].barBased;

    if (
      selectedMappingEntries.every((entry) => entry.barBased === firstBarBased)
    ) {
      return firstBarBased;
    }

    return null;
  }

  get bulkUserCategory(): CentralMappingCategory | null {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    const firstUserCategory = selectedMappingEntries[0].userCategory;

    if (
      selectedMappingEntries.every(
        (entry) => entry.userCategory === firstUserCategory,
      )
    ) {
      return firstUserCategory;
    }

    return null;
  }

  get bulkUserCommissionable(): Commissionable {
    const { selectedMappingEntriesHasCommissionable: selectedMappingEntries } =
      this;

    if (!selectedMappingEntries[0]) {
      return null;
    }

    // Edge case: when the commissionable values are the same, but it's a mix of user-mapped and auto-mapped,
    // the bulk edit dropdown should show it as user-mapped. That's why we check on `commissionable` here.
    const firstCommissionable = selectedMappingEntries[0].commissionable;

    if (
      selectedMappingEntries.every(
        (entry) => entry.commissionable === firstCommissionable,
      )
    ) {
      return firstCommissionable;
    }

    return null;
  }

  get description(): { isMixed: boolean; value: string } {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return { value: '', isMixed: false };
    }

    const firstDescription = selectedMappingEntries[0].description;

    if (
      selectedMappingEntries.every(
        (entry) => entry.description === firstDescription,
      )
    ) {
      return {
        isMixed: false,
        value: firstDescription,
      };
    }

    return {
      isMixed: true,
      value: '',
    };
  }

  get isChannelBreakdown(): boolean {
    return this.args.breakdown === BREAKDOWNS.DISTRIBUTION_CHANNEL;
  }

  get isMarketSegmentBreakdown(): boolean {
    return this.args.breakdown === BREAKDOWNS.MARKET_SEGMENT;
  }

  get isMixedBarBased(): boolean {
    const { selectedMappingEntriesHasBarBased: selectedMappingEntries } = this;

    if (!selectedMappingEntries[0]) {
      return false;
    }

    const firstBarBased = selectedMappingEntries[0].barBased;

    if (
      selectedMappingEntries.every((entry) => entry.barBased === firstBarBased)
    ) {
      return false;
    }

    return true;
  }

  get isMixedCategory(): boolean {
    const { selectedMappingEntries } = this.args;

    if (!selectedMappingEntries[0]) {
      return false;
    }

    const firstCategory = selectedMappingEntries[0].category;

    if (
      selectedMappingEntries.every((entry) => entry.category === firstCategory)
    ) {
      return false;
    }

    return true;
  }

  get isMixedCommissionable(): boolean {
    const { selectedMappingEntriesHasCommissionable: selectedMappingEntries } =
      this;

    if (!selectedMappingEntries[0]) {
      return false;
    }

    const firstCommissionable = selectedMappingEntries[0].commissionable;

    if (
      selectedMappingEntries.every(
        (entry) => entry.commissionable === firstCommissionable,
      )
    ) {
      return false;
    }

    return true;
  }

  get isRatePlanValueType(): boolean {
    return this.args.valueType === PMS_VALUE_TYPES.RATE_CODE;
  }

  get isRoomTypeBreakdown(): boolean {
    return this.args.breakdown === BREAKDOWNS.ROOM_TYPE;
  }

  get maxAdults(): {
    isMixed?: boolean;
    value: number | null;
  } {
    const selectedMappingEntries = this.args.selectedMappingEntries.filter(
      (entry) =>
        // @ts-expect-error: Incorrect type
        isMappingEntryRoomType(entry),
    );

    if (!selectedMappingEntries[0]) {
      return {
        value: null,
      };
    }

    // The bulk value of maxAdults is the common value in all selected mapping entries.
    // If there is no common value, the value is null and the dropdown will display "Mixed".
    const firstMaxAdults = selectedMappingEntries[0].maxAdults;

    if (
      selectedMappingEntries.every(
        (entry) => entry.maxAdults === firstMaxAdults,
      )
    ) {
      return {
        value: firstMaxAdults,
      };
    }

    return {
      value: null,
      isMixed: true,
    };
  }

  // Getter used to narrow the type of CentralMappingMappingEntryExtendedModel[],
  // otherwise we can't access the `userBarBased` or `autoCategory` properties.
  get selectedMappingEntriesHasBarBased(): CentralMappingMappingEntryMarketSegment[] {
    return this.args.selectedMappingEntries.filter((mappingEntry) =>
      // @ts-expect-error: Incorrect type
      mappingEntryHasBarBased(mappingEntry),
    );
  }

  get selectedMappingEntriesHasCommissionable(): CentralMappingMappingEntryMarketSegment[] {
    return this.args.selectedMappingEntries.filter((mappingEntry) =>
      mappingEntryHasCommissionable(mappingEntry),
    );
  }

  get selectedMappingEntriesHasCostProfile(): CentralMappingMappingEntryChannel[] {
    return this.args.selectedMappingEntries.filter((mappingEntry) =>
      mappingEntryHasCostProfile(mappingEntry),
    );
  }

  trackBulkUpdate(updatedColumn: ColumnProperties): void {
    const rowCount = new Set(
      this.args.selectedMappingEntries.map((entry) => {
        return entry.value;
      }),
    ).size;

    this.usageAnalytics.track('Mapping Bulk Updated', {
      eventTrigger: 'bulk-changes',
      bulk: updatedColumn,
      rowCount,
    });

    this.centralMapping.trackMappingEntryChanged({
      mappingChange: updatedColumn,
      rowCount,
      rowType: 'bulk',
    });
  }

  @action setBarBased(value: BarBased | null): void {
    this.args.barBased.setBarBased(value);
    this.trackBulkUpdate(COLUMN_PROPERTIES.BAR_BASED);
  }

  @action setCategory(category: CentralMappingCategory | null): void {
    this.args.mapping.setCategory(category);
    this.trackBulkUpdate(COLUMN_PROPERTIES.MAPPING);
  }

  @action setCommissionable(value: Commissionable): void {
    this.args.commissionable.setCommissionable(value);
    this.trackBulkUpdate(COLUMN_PROPERTIES.COMMISSIONABLE);
  }

  @action setCostProfile(costProfile: CostSettingProfile | null): void {
    this.args.costProfile.setCostProfile(costProfile);
    this.trackBulkUpdate(COLUMN_PROPERTIES.COST_PROFILE);
  }

  @action setDescription(value: string): void {
    this.args.selectedMappingEntries.forEach((entry) => {
      entry.description = value;
    });
    this.trackBulkUpdate(COLUMN_PROPERTIES.DESCRIPTION);
  }

  @action setMaxAdultsValue(value: number | null): void {
    this.args.selectedMappingEntries.forEach((entry) => {
      // @ts-expect-error: Incorrect type
      if (isMappingEntryRoomType(entry)) {
        entry.maxAdults = value;
      }
    });
    this.trackBulkUpdate(COLUMN_PROPERTIES.MAX_ADULTS);
  }

  <template>
    <th class="bulk-edit-row-cell">
      <PrismBadge
        @label={{t
          "settings.central_mapping.mapping.selected.count"
          class="text-200--bold"
          count=@bulkEditRow.numberOfSelectedRows
          htmlSafe=true
        }}
        @type="info"
      />
    </th>

    <th
      class="bulk-edit-row-cell py-0"
      data-test-id="bulk-edit-row-cell-description"
    >
      <SettingsCentralMappingTablesCellsEditableTextCell
        @cellDataTestId="bulk-action-description"
        @isMixed={{this.description.isMixed}}
        @setValue={{this.setDescription}}
        @value={{this.description.value}}
      />
    </th>

    <th class="bulk-edit-row-cell" data-test-id="bulk-edit-row-cell-property" />

    <th class="bulk-edit-row-cell" data-test-id="bulk-edit-row-cell-mapping">
      <SettingsCentralMappingDropdownsDropdownMapping
        @autoCategory={{this.bulkAutoCategory}}
        @category={{this.bulkCategory}}
        @hideAutomappingVisuals={{true}}
        @hierarchyCategories={{@mapping.hierarchyCategories}}
        @isMixed={{this.isMixedCategory}}
        @setCategory={{this.setCategory}}
        @userCategory={{this.bulkUserCategory}}
        @variant="default"
      />
    </th>

    {{#if this.isMarketSegmentBreakdown}}
      <th
        class="bulk-edit-row-cell"
        data-test-id="bulk-edit-row-cell-bar-based"
      >
        <SettingsCentralMappingDropdownsDropdownBarBased
          @autoBarBased={{this.bulkAutoBarBased}}
          @barBased={{this.bulkBarBased}}
          @hideAutomappingVisuals={{true}}
          @isMixed={{this.isMixedBarBased}}
          @setBarBased={{this.setBarBased}}
          @userBarBased={{this.bulkUserBarBased}}
          @variant="default"
        />
      </th>

      {{#if this.isRatePlanValueType}}
        <th
          class="bulk-edit-row-cell"
          data-test-id="bulk-edit-row-cell-commissionable"
        >
          <SettingsCentralMappingDropdownsDropdownCommissionable
            @autoCommissionable={{this.bulkAutoCommissionable}}
            @commissionable={{this.bulkCommissionable}}
            @hideAutomappingVisuals={{true}}
            @isMixed={{this.isMixedCommissionable}}
            @setCommissionable={{this.setCommissionable}}
            @userCommissionable={{this.bulkUserCommissionable}}
            @variant="default"
          />
        </th>
      {{/if}}
    {{else if this.isChannelBreakdown}}
      <th
        class="bulk-edit-row-cell"
        data-test-id="bulk-edit-row-cell-cost-profile"
      >
        <SettingsCentralMappingDropdownsDropdownCostProfile
          @autoCostProfile={{this.aggregatedCostProfile.autoCostProfile}}
          @costProfile={{this.aggregatedCostProfile.costProfile}}
          @hideAutomappingVisuals={{true}}
          @isMixed={{this.aggregatedCostProfile.isMixed}}
          @onNavigateToCostSettings={{@costProfile.onNavigateToCostSettings}}
          @profiles={{@costProfile.profiles}}
          @setCostProfile={{this.setCostProfile}}
          @userCostProfile={{this.aggregatedCostProfile.userCostProfile}}
          @variant="default"
        />
      </th>
    {{else if this.isRoomTypeBreakdown}}
      {{! Room type breakdown - Inventory }}
      <th class="bulk-edit-row-cell" />

      {{! Room type breakdown - No. Adults }}
      <th
        class="bulk-edit-row-cell py-0"
        data-test-id="bulk-edit-row-cell-max-adults"
      >
        <SettingsCentralMappingTablesCellsEditableNumberCell
          @cellDataTestId="bulk-action-max-adults"
          @isMixed={{this.maxAdults.isMixed}}
          @maxValue={{MAX_ADULTS_IN_ROOM}}
          @placeholder={{t
            "settings.central_mapping.mapping.max_adults.placeholder"
          }}
          @setValue={{this.setMaxAdultsValue}}
          @value={{this.maxAdults.value}}
        />
      </th>
    {{/if}}
  </template>
}
